import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import {
  AlertTriangle, Plus, Search, Filter, Edit3, Trash2, Eye, Save, RefreshCw,
  CheckCircle, Clock, X, Target, Activity, Users, MessageSquare,
  Calendar, FileText, Database, Server, Shield, Bell, Download, Upload,
  Settings, TestTube, BarChart3, TrendingUp, PieChart, Globe, Lock,
  Copy, ExternalLink, Zap, Star, Heart, Coffee, Pizza, Cake, Music,
  Camera, Video, File, Share, Bookmark, Flag, Monitor, Cpu, HardDrive,
  Network, Wifi, Signal, Battery, Volume, Volume2, VolumeX, Play,
  Pause, Stop, SkipBack, SkipForward, RotateCcw, RotateCw, Maximize,
  Minimize, Maximize2, Minimize2, Square, Circle, Triangle, Hexagon,
  Octagon, Pentagon, Diamond, Star as StarIcon, PieChart as PieChartIcon
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';

const EscalationRules = () => {
  const [escalationRules, setEscalationRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [testingRule, setTestingRule] = useState(false);

  useEffect(() => {
    const fetchEscalationRules = async () => {
      try {
        setLoading(true);
        const response = await MockDataService.getEscalationRules();
        if (response.success) {
          setEscalationRules(response.data);
        } else {
          setError('Failed to load escalation rules');
        }
      } catch (err) {
        setError('Error loading escalation rules');
        console.error('Escalation rules error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEscalationRules();
  }, []);

  const handleCreateRule = () => {
    setSelectedRule(null);
    setShowForm(true);
  };

  const handleEditRule = (rule) => {
    setSelectedRule(rule);
    setShowForm(true);
  };

  const handleTestRule = async (ruleId) => {
    setTestingRule(true);
    try {
      // Simulate escalation rule testing
      await new Promise(resolve => setTimeout(resolve, 2000));
      alert('Escalation rule test completed successfully!');
    } catch (err) {
      console.error('Escalation rule test failed:', err);
      alert('Escalation rule test failed. Please check your configuration.');
    } finally {
      setTestingRule(false);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (window.confirm('Are you sure you want to delete this escalation rule?')) {
      try {
        const updatedRules = escalationRules.filter(rule => rule.id !== ruleId);
        setEscalationRules(updatedRules);
        //console.log(`Deleted escalation rule ${ruleId}`);
      } catch (err) {
        console.error('Failed to delete escalation rule:', err);
      }
    }
  };

  const handleToggleStatus = async (ruleId) => {
    try {
      const updatedRules = escalationRules.map(rule =>
        rule.id === ruleId 
          ? { ...rule, isActive: !rule.isActive }
          : rule
      );
      setEscalationRules(updatedRules);
    } catch (err) {
      console.error('Failed to toggle escalation rule status:', err);
    }
  };

  const filteredRules = escalationRules.filter(rule => {
    const matchesSearch = rule.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         rule.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || rule.type === filterType;
    return matchesSearch && matchesType;
  });

  const getStatusColor = (isActive) => {
    return isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'response': return 'bg-blue-100 text-blue-800';
      case 'resolution': return 'bg-red-100 text-red-800';
      case 'priority': return 'bg-orange-100 text-orange-800';
      case 'assignment': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading escalation rules...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 mx-auto mb-4 text-red-600" />
          <p className="text-red-600">{error}</p>
          <Button onClick={() => window.location.reload()} className="mt-4">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Escalation Rules</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Configure escalation rules for SLA violations
          </p>
        </div>
        <Button onClick={handleCreateRule} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Create Escalation Rule
        </Button>
      </div>

      {/* Search and Filter */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search escalation rules..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="response">Response</SelectItem>
            <SelectItem value="resolution">Resolution</SelectItem>
            <SelectItem value="priority">Priority</SelectItem>
            <SelectItem value="assignment">Assignment</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Escalation Rules List */}
      <div className="grid gap-4">
        {filteredRules.map((rule) => (
          <Card key={rule.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                      {rule.name}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {rule.description}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={getStatusColor(rule.isActive)}>
                        {rule.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      <Badge className={getTypeColor(rule.type)}>
                        {rule.type}
                      </Badge>
                      <span className="text-xs text-gray-500">
                        Created: {new Date(rule.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTestRule(rule.id)}
                    disabled={testingRule}
                  >
                    <TestTube className="h-4 w-4 mr-1" />
                    {testingRule ? 'Testing...' : 'Test'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleStatus(rule.id)}
                  >
                    {rule.isActive ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditRule(rule)}
                  >
                    <Edit3 className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteRule(rule.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {/* Escalation Triggers */}
              <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Escalation Triggers
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Trigger Time:</span>
                    <p className="font-medium">{rule.triggerTime}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Trigger Condition:</span>
                    <p className="font-medium">{rule.triggerCondition}</p>
                  </div>
                </div>
              </div>

              {/* Escalation Actions */}
              <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Escalation Actions
                </h4>
                <div className="space-y-2">
                  {rule.actions.map((action, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      <Badge variant="outline">{action.type}</Badge>
                      <span>{action.description}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recipients */}
              {rule.recipients && rule.recipients.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Notification Recipients
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {rule.recipients.map((recipient, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {recipient.type}: {recipient.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredRules.length === 0 && (
        <div className="text-center py-12">
          <AlertTriangle className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No escalation rules found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {searchQuery || filterType !== 'all' 
              ? 'Try adjusting your search or filter criteria.'
              : 'Get started by creating your first escalation rule.'
            }
          </p>
          {(!searchQuery && filterType === 'all') && (
            <Button onClick={handleCreateRule}>
              <Plus className="h-4 w-4 mr-2" />
              Create Escalation Rule
            </Button>
          )}
        </div>
      )}

      {/* Escalation Rule Form Modal */}
      {showForm && (
        <EscalationRuleForm
          rule={selectedRule}
          onClose={() => setShowForm(false)}
          onSave={(ruleData) => {
            if (selectedRule) {
              // Update existing rule
              const updatedRules = escalationRules.map(rule =>
                rule.id === selectedRule.id ? { ...rule, ...ruleData } : rule
              );
              setEscalationRules(updatedRules);
            } else {
              // Add new rule
              const newRule = {
                id: Date.now().toString(),
                ...ruleData,
                isActive: true,
                createdAt: new Date().toISOString()
              };
              setEscalationRules([...escalationRules, newRule]);
            }
            setShowForm(false);
          }}
        />
      )}
    </div>
  );
};

// Escalation Rule Form Component
const EscalationRuleForm = ({ rule, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: rule?.name || '',
    description: rule?.description || '',
    type: rule?.type || 'response',
    triggerTime: rule?.triggerTime || '',
    triggerCondition: rule?.triggerCondition || '',
    actions: rule?.actions || [
      { type: 'notification', description: 'Send notification to assigned agent' }
    ],
    recipients: rule?.recipients || [],
    isActive: rule?.isActive || true
  });

  const [newAction, setNewAction] = useState({ type: 'notification', description: '' });
  const [newRecipient, setNewRecipient] = useState({ type: 'user', name: '' });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddAction = () => {
    if (newAction.type && newAction.description) {
      setFormData(prev => ({
        ...prev,
        actions: [...prev.actions, newAction]
      }));
      setNewAction({ type: 'notification', description: '' });
    }
  };

  const handleRemoveAction = (index) => {
    setFormData(prev => ({
      ...prev,
      actions: prev.actions.filter((_, i) => i !== index)
    }));
  };

  const handleAddRecipient = () => {
    if (newRecipient.type && newRecipient.name) {
      setFormData(prev => ({
        ...prev,
        recipients: [...prev.recipients, newRecipient]
      }));
      setNewRecipient({ type: 'user', name: '' });
    }
  };

  const handleRemoveRecipient = (index) => {
    setFormData(prev => ({
      ...prev,
      recipients: prev.recipients.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {rule ? 'Edit Escalation Rule' : 'Create Escalation Rule'}
            </h2>
            <Button variant="ghost" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Rule Name *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., High Priority Response Escalation"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Rule Type *
                </label>
                <Select value={formData.type} onValueChange={(value) => handleInputChange('type', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="response">Response Escalation</SelectItem>
                    <SelectItem value="resolution">Resolution Escalation</SelectItem>
                    <SelectItem value="priority">Priority Escalation</SelectItem>
                    <SelectItem value="assignment">Assignment Escalation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Describe the escalation rule..."
                className="w-full h-24 p-3 border border-gray-300 dark:border-gray-600 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                required
              />
            </div>

            {/* Trigger Configuration */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Trigger Time *
                </label>
                <Input
                  value={formData.triggerTime}
                  onChange={(e) => handleInputChange('triggerTime', e.target.value)}
                  placeholder="e.g., 2 hours, 1 day"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Trigger Condition *
                </label>
                <Select value={formData.triggerCondition} onValueChange={(value) => handleInputChange('triggerCondition', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select condition" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no_response">No Response</SelectItem>
                    <SelectItem value="no_resolution">No Resolution</SelectItem>
                    <SelectItem value="priority_change">Priority Change</SelectItem>
                    <SelectItem value="assignment_failed">Assignment Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Escalation Actions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Escalation Actions
              </label>
              <div className="space-y-2">
                {formData.actions.map((action, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 border rounded-lg">
                    <Badge variant="outline">{action.type}</Badge>
                    <span className="text-sm flex-1">{action.description}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveAction(index)}
                      className="text-red-600"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <Select value={newAction.type} onValueChange={(value) => setNewAction(prev => ({ ...prev, type: value }))}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="notification">Notification</SelectItem>
                      <SelectItem value="assignment">Assignment</SelectItem>
                      <SelectItem value="priority_change">Priority Change</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    value={newAction.description}
                    onChange={(e) => setNewAction(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Enter action description"
                    className="flex-1"
                  />
                  <Button type="button" variant="outline" onClick={handleAddAction}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Recipients */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Notification Recipients
              </label>
              <div className="space-y-2">
                {formData.recipients.map((recipient, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 border rounded-lg">
                    <Badge variant="outline">{recipient.type}</Badge>
                    <span className="text-sm flex-1">{recipient.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveRecipient(index)}
                      className="text-red-600"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <Select value={newRecipient.type} onValueChange={(value) => setNewRecipient(prev => ({ ...prev, type: value }))}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="role">Role</SelectItem>
                      <SelectItem value="department">Department</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    value={newRecipient.name}
                    onChange={(e) => setNewRecipient(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter recipient name"
                    className="flex-1"
                  />
                  <Button type="button" variant="outline" onClick={handleAddRecipient}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="isActive" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Rule is active
              </label>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-6 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit">
                <Save className="h-4 w-4 mr-2" />
                {rule ? 'Update Rule' : 'Create Rule'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EscalationRules;
